import { BalanceQuoterABI } from '../components/abi/BalanceQuoterABI';
import { useReadContracts } from 'wagmi';
import { CHAINS } from '@dapp/multi-lib/network';
import { QueryOptions } from 'utils/useQueryUtils';

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000' as const;

// USDC addresses per chain
const USDC_ADDRESSES: Record<number, `0x${string}`> = {
  [CHAINS.BASE_L2]: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  [CHAINS.ETH_MAINNET]: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',
  [CHAINS.BSC_MAINNET]: '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d',
};

// BalanceQuoter contract addresses per chain.
// Zero address = not yet deployed on this chain, requests will be skipped.
const QUOTER_ADDRESSES: Record<number, `0x${string}`> = {
  [CHAINS.BASE_L2]: '0x9B96CB4F5Dbf666E7B799cbCa07a362180952c36',
  [CHAINS.ETH_MAINNET]: ZERO_ADDRESS, // TODO: fill when deployed
  [CHAINS.BSC_MAINNET]: ZERO_ADDRESS, // TODO: fill when deployed
};

export interface TokenInput {
  address: `0x${string}`;
  chainId: number;
}

export interface AssetsData {
  map: Record<
    number,
    Record<`0x${string}`, { balance: bigint; quote: bigint }>
  >;
  quotesTotal: bigint;
  decimals: number;
}

export const useUserTotalAssets = (
  params: {
    userAddress: `0x${string}`;
    tokens: TokenInput[];
  } & QueryOptions
) => {
  const { userAddress, tokens, query = {} } = params;

  // Group by chainId, skip chains where quoter is not yet deployed
  const tokensByChain = tokens.reduce(
    (acc, token) => {
      const quoter = QUOTER_ADDRESSES[token.chainId];
      if (!quoter || quoter === ZERO_ADDRESS) return acc;
      if (!acc[token.chainId]) acc[token.chainId] = [];
      acc[token.chainId].push(token.address);
      return acc;
    },
    {} as Record<number, `0x${string}`[]>
  );

  const chainIds = Object.keys(tokensByChain).map(Number);

  const contracts = chainIds.map((chainId) => ({
    abi: BalanceQuoterABI,
    address: QUOTER_ADDRESSES[chainId],
    functionName: 'quoteBalancesFor' as const,
    chainId,
    args: [
      userAddress,
      USDC_ADDRESSES[chainId],
      tokensByChain[chainId],
    ] as const,
  }));

  const queryOptions = {
    refetchInterval: 15000,
    staleTime: 15000,
    ...query,
    enabled: query.enabled !== false && !!userAddress && tokens.length > 0,
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore wait for strictmode fix
  return useReadContracts({
    contracts,
    query: {
      ...queryOptions,
      select: (results): AssetsData => {
        const map: Record<
          number,
          Record<`0x${string}`, { balance: bigint; quote: bigint }>
        > = {};
        let quotesTotal = BigInt(0);

        chainIds.forEach((chainId, i) => {
          const result = results[i];
          if (result.status !== 'success' || !result.result) return;

          const [balances, quotes] = result.result;
          const addresses = tokensByChain[chainId];

          map[chainId] = {};
          addresses.forEach((address, j) => {
            map[chainId][address] = {
              balance: balances[j],
              quote: quotes[j],
            };
            quotesTotal += quotes[j];
          });
        });

        return { map, quotesTotal, decimals: 6 };
      },
    },
  });
};
